<template>
  <div class="image-upload" :class="{ 'is-disabled': disabled, 'hide-upload-trigger': hideUploadTrigger }" @click="handleClick">
    <!-- 多图模式：picture-card -->
    <template v-if="multiple">
      <el-upload
        action=""
        list-type="picture-card"
        :multiple="true"
        :limit="limit"
        :file-list="fileList"
        :http-request="handleMultiUpload"
        :on-remove="handleMultiRemove"
        :on-exceed="handleMultiExceed"
        :on-preview="handlePictureCardPreview"
        :accept="accept"
        :disabled="disabled"
      >
        <i class="el-icon-plus"></i>
      </el-upload>
    </template>

    <!-- 单图模式：原有逻辑 -->
    <template v-else>
      <el-upload class="image-uploader" :action="''" :show-file-list="false" :http-request="handleUpload" :before-upload="beforeUpload" :disabled="disabled" :accept="accept">
        <div v-if="imageUrl" class="upload-image-wrapper" :style="imageWrapperStyle">
          <img :src="imageUrl" class="upload-image-inner" />
          <div class="upload-image-actions">
            <span class="upload-image-preview" @click.stop="handleSinglePreview">
              <i class="el-icon-zoom-in"></i>
            </span>
            <span v-if="!disabled" class="upload-image-delete" @click.stop="handleSingleRemove">
              <i class="el-icon-delete"></i>
            </span>
          </div>
        </div>
        <div v-else class="upload-placeholder" :style="placeholderStyle">
          <i class="el-icon-plus" style="font-size: 28px; color: #bbb"></i>
        </div>
      </el-upload>
      <div v-if="tip" class="upload-tip">{{ tip }}</div>
    </template>

    <!-- 图片大图预览弹窗 -->
    <el-dialog :visible.sync="dialogVisible" append-to-body>
      <img width="100%" :src="dialogImageUrl" alt="" />
    </el-dialog>
  </div>
</template>

<script>
import { uploadFile, resolveFileUrl } from "@/utils/oss";

export default {
  name: "ImageUpload",
  props: {
    value: {
      type: [String, Array],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: ".jpg,.jpeg,.png",
    },
    dir: {
      type: String,
      default: "images",
    },
    /** 图片容器尺寸，如 "120px" 或 { width: '120px', height: '120px' } */
    size: {
      type: [String, Object],
      default: "120px",
    },
    tip: {
      type: String,
      default: "",
    },
    /** 多图模式，value 应为 Array */
    multiple: {
      type: Boolean,
      default: false,
    },
    /** 多图模式下的最大数量 */
    limit: {
      type: Number,
      default: 5,
    },
  },
  emits: ["input", "success", "error", "preview"],
  data() {
    return {
      uploading: false,
      /** 多图模式：内部 uid→url 映射，用于删除时精确定位 */
      multiFileList: [],
      /** 大图预览 */
      dialogVisible: false,
      dialogImageUrl: "",
    };
  },
  computed: {
    imageUrl() {
      return resolveFileUrl(this.value || "");
    },
    fileList() {
      if (!this.multiple) return [];
      return this.multiFileList.map((f, i) => ({ name: `img_${i}`, url: resolveFileUrl(f.url), uid: f.uid }));
    },
    placeholderStyle() {
      const size = typeof this.size === "object" ? this.size : { width: this.size, height: this.size };
      return {
        width: size.width || "120px",
        height: size.height || "120px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed #c0ccda",
        borderRadius: "6px",
        background: "#fbfdff",
        cursor: this.disabled ? "not-allowed" : "pointer",
      };
    },
    imageStyle() {
      const size = typeof this.size === "object" ? this.size : { width: this.size, height: this.size };
      return {
        width: size.width || "120px",
        height: size.height || "120px",
        objectFit: "cover",
        borderRadius: "6px",
      };
    },
    /** 单图模式下图片外层包裹容器样式 */
    imageWrapperStyle() {
      const size = typeof this.size === "object" ? this.size : { width: this.size, height: this.size };
      return {
        width: size.width || "120px",
        height: size.height || "120px",
        position: "relative",
        borderRadius: "6px",
        overflow: "hidden",
      };
    },
    /** 多图模式下：disabled 且已有图片时才隐藏上传触发框，无图片时仍显示但不可点击 */
    hideUploadTrigger() {
      return this.multiple && this.disabled && this.multiFileList.length > 0;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (!this.multiple) return;
        const urls = (Array.isArray(val) ? val : val ? val.split(",").filter(Boolean) : []).map((u) => resolveFileUrl(u));
        const trackedUrls = new Set(this.multiFileList.map((f) => f.url));
        const newEntries = urls.filter((url) => !trackedUrls.has(url)).map((url, i) => ({ uid: `init_${Date.now()}_${i}`, url }));
        this.multiFileList = [...this.multiFileList.filter((f) => urls.includes(f.url)), ...newEntries];
      },
    },
  },
  methods: {
    beforeUpload(file) {
      const isValidType = /\.(jpg|jpeg|png)$/i.test("." + file.name.split(".").pop());
      if (!isValidType) {
        this.$message.error("只支持 JPG、JPEG 或 PNG 格式的图片文件");
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error("图片大小不能超过 5MB");
        return false;
      }
      return true;
    },
    async handleUpload(options) {
      this.uploading = true;
      try {
        const url = await uploadFile(options.file);
        this.$emit("input", url);
        this.$emit("success", url);
        this.$message.success("上传成功");
      } catch (err) {
        this.$emit("error", err);
        this.$message.error("上传失败，请稍后重试");
      } finally {
        this.uploading = false;
      }
    },
    handleClick() {
      if (this.disabled && this.imageUrl) {
        this.$emit("preview", this.imageUrl);
      }
    },
    /** 单图模式：预览大图 */
    handleSinglePreview() {
      this.dialogImageUrl = this.imageUrl;
      this.dialogVisible = true;
    },
    /** 单图模式：删除图片 */
    handleSingleRemove() {
      this.$emit("input", "");
      this.$message.success("图片已删除");
    },
    // ===== 多图模式方法 =====
    async handleMultiUpload(options) {
      try {
        const url = await uploadFile(options.file);
        this.multiFileList.push({ uid: options.file.uid, url });
        this.$emit(
          "input",
          this.multiFileList.map((f) => f.url),
        );
        this.$emit("success", url);
        options.onSuccess({ url }, options.file);
        this.$message.success("上传成功");
      } catch (err) {
        this.$emit("error", err);
        this.$message.error("上传失败，请稍后重试");
        options.onError(err);
      }
    },
    handleMultiRemove(file) {
      this.multiFileList = this.multiFileList.filter((f) => f.uid !== file.uid);
      this.$emit(
        "input",
        this.multiFileList.map((f) => f.url),
      );
    },
    handleMultiExceed() {
      this.$message.warning(`最多只能上传${this.limit}张图片`);
    },
    handlePictureCardPreview(file) {
      this.dialogImageUrl = resolveFileUrl(file.url);
      this.dialogVisible = true;
    },
  },
};
</script>

<style scoped>
.image-upload {
  display: inline-block;
}

/* ===== 单图模式：图片容器与悬浮操作层 ===== */
.upload-image-wrapper {
  position: relative;
  cursor: pointer;
}

.upload-image-inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.upload-image-actions {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  cursor: default;
  text-align: center;
  color: #fff;
  opacity: 0;
  font-size: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-image-wrapper:hover .upload-image-actions {
  opacity: 1;
}

.upload-image-actions span {
  cursor: pointer;
  margin: 0 8px;
}

/* ===== 原有样式 ===== */
.upload-image {
  display: block;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  line-height: 1.4;
}

.upload-placeholder:hover {
  border: 1px dashed #409eff !important;
  color: #409eff;
}

/* disabled 且已有图片时隐藏 picture-card 模式的上传触发框，无图片时仍显示但不可点击 */
.image-upload.hide-upload-trigger ::v-deep .el-upload--picture-card {
  display: none;
}

/* disabled 无图片时 picture-card 触发框显示禁用光标 */
.image-upload.is-disabled:not(.hide-upload-trigger) ::v-deep .el-upload--picture-card {
  cursor: not-allowed;
}
</style>
